import { useRef } from "react";
import "./index.scss";

import Chart from "../chart";
import ChartSingleBar from "../chartSingleBar";
import Matriz from "../../../../components/matriz";
import { decodeJWT, getToken } from "../../../../services/utils";
import BlueBars from "../../../../assets/images/blue-bars.png";
import LogoPlanejadoraHorizontal from "../../../../assets/images/logo-planejadora.png";
import LogoPlanejadoraGrande from "../../../../assets/images/logo-planejadora-grande.png";

const PdfIndicators = ({ company, info, planningTitle, planId, monthId, pcDate, indicators, getIndicator, getChart, renderHeaderInfo }) => {
  const ref = useRef(null);
  const { cpf: accountCpf, name: accountName } = decodeJWT(getToken()) || {};
  const issuedAt = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
  }).format(new Date());
  const coverName =
    info?.cnpj_name ||
    info?.cpf_name ||
    company?.name ||
    accountName;
  const documentId =
    info?.cnpj ||
    info?.cpf ||
    company?.cnpj ||
    accountCpf;

  const formatDocument = (value) => {
    if (!value) return "";

    const digits = String(value).replace(/\D/g, "");

    if (digits.length === 11) {
      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
    }

    if (digits.length === 14) {
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    }

    return value;
  };

  const formatCurrency = (value) =>
    Number(value)?.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "0,00";

  const renderValues = (key, percent = false, currency = false) => {
    const ind = getIndicator(key);
    if (!ind) return null;
    const formatValue = (n) => {
      if (n == null) return "--";
      if (percent) return `${Number(n).toFixed(2)}%`;
      if (currency) return `R$ ${formatCurrency(n)}`;
      return n;
    };

    // NOTA: Remover isso após evento SEBRAE 02/03
    const isInadimplencia = key === "inadimplencia";

    return (
      <div className="pdf-values">
        {!isInadimplencia && (
          <div>
            <h4>Projetada:</h4>
            <p>
              {ind?.projetada != null
                ? formatValue(ind.projetada)
                : "--"}
            </p>
          </div>
        )}
        <div>
          <h4>Alcançada:</h4>
          <p>
            {formatValue(ind?.alcancada) ?? "--"}
          </p>
          <p className={ind.status === "above" ? "above" : "below"}>
            {ind?.alcancada !== ind?.projetada && (
              <p
                className={ind?.alcancada > ind?.projetada ? "above" : "below"}
              >
                {ind?.alcancada > ind?.projetada
                  ? "Acima do projetado"
                  : "Abaixo do projetado"}
              </p>
            )}
          </p>
        </div>
      </div>
    );
  };

  const renderPageDecoration = () => (
    <div className="pdf-page-decoration">
      <img
        src={BlueBars}
        alt="Barras decorativas azuis"
        className="pdf-page-decoration__bars"
      />
      <img
        src={LogoPlanejadoraHorizontal}
        alt="Planejadora SEBRAE"
        className="pdf-page-decoration__logo"
      />
      <img
        src={BlueBars}
        alt="Barras decorativas azuis"
        className="pdf-page-decoration__bars-bottom"
      />
      <div className="pdf-page-decoration__footer-meta">
        <span className="pdf-page-decoration__footer-id">ID: {planId}</span>
        <span className="pdf-page-decoration__footer-monthly-id">ID Mensal: {monthId}</span>
      </div>
    </div>
  );

  return (
    <div id="pdf-indicators" ref={ref}>
      <section className="pdf-section pdf-section--cover">
        <div className="cover-page">
          <div className="top-shape">
            <img
                src={BlueBars}
                alt="Topo"
                className="top-shape-img"
            />
          </div>

          <div className="content">
            <img src={LogoPlanejadoraGrande} alt="Planejadora SEBRAE" className="logo" />

            <div className="subtitle">
              <strong>Monitoramento Mensal</strong> <span className="subtitle__gray">(Pós Crédito)</span>{" "}
              <span className="month">{pcDate}</span>
            </div>

            <div className="title">{planningTitle}</div>

            <div className="company-info">
              {coverName}
              <br />
              {formatDocument(documentId)}
            </div>
          </div>

          <div className="issue-date">Emitido em: {issuedAt}</div>

          <div className="bottom-shape">
            <img
                src={BlueBars}
                alt="Topo"
                className="bottom-shape-img"
            />
          </div>

          <div className="document-footer">
            <div className="document-id">ID: {planId}</div>
            <div className="monthly-id">ID Mensal: {monthId}</div>
          </div>
        </div>
      </section>
      <section className="pdf-section pdf-section--content">
        {renderPageDecoration()}
        <div className="pdf-header">
          <div className="pdf-divider">
            <h2>Indicadores de Desempenho</h2>
          </div>
        </div>

        <h2>Capacidade de Pagamento</h2>
        {renderValues("capacidadePagamento", false, true)}
        <div className="pdf-chart-box">
          <ChartSingleBar
            indicator={getIndicator("capacidadePagamento")}
            type="money"
          />
        </div>

        <h2>Receita Mensal</h2>
        {renderValues("receitaMensal", false, true)}
        <div className="pdf-chart-box">
          <Chart data={getChart("receitaMensal")} type="money" />
        </div>
      </section>

      <section className="pdf-section pdf-section--content">
        {renderPageDecoration()}
        <div className="pdf-header">
          <div className="pdf-divider">
            <h2>Indicadores de Desempenho</h2>
          </div>
        </div>

        <h2>Lucro Mensal</h2>
        {renderValues("lucroMensal", false, true)}
        <div className="pdf-chart-box">
          <Chart data={getChart("lucroMensal")} type="money" />
        </div>

        <h2>Lucratividade</h2>
        {renderValues("lucratividade", true, false)}
        <div className="pdf-chart-box">
          <Chart data={getChart("lucratividade")} type="percent" />
        </div>
      </section>

      <section className="pdf-section pdf-section--content">
        {renderPageDecoration()}
        <div className="pdf-header">
          <div className="pdf-divider">
            <h2>Indicadores de Desempenho</h2>
          </div>
        </div>

        <h2>Rentabilidade</h2>
        {renderValues("rentabilidade", true, false)}
        <div className="pdf-chart-box">
          <Chart data={getChart("rentabilidade")} type="percent" />
        </div>

        <h2>Necessidade de Capital de Giro</h2>
        {renderValues("ncg", false, true)}
        <div className="pdf-chart-box">
          <Chart data={getChart("ncg")} />
        </div>
      </section>

      <section className="pdf-section pdf-section--content">
        {renderPageDecoration()}
        <div className="pdf-header">
          <div className="pdf-divider">
            <h2>Indicadores de Desempenho</h2>
          </div>
        </div>

        <h2>Necessidade de Formação de Capital de Giro</h2>
        {renderValues("nfcg", false, true)}
        <div className="pdf-chart-box">
          <Chart data={getChart("nfcg")} />
        </div>
      </section>
      <section className="pdf-section pdf-section--content">
        {renderPageDecoration()}
        <div className="pdf-header">
          <div className="pdf-divider">
            <h2>Indicadores de Endividamento</h2>
          </div>
        </div>

        <h2>Matriz de Avaliação Econômica</h2>
        <div className="pdf-matriz-box">
          <Matriz cml={indicators.matriz?.cml} gee={indicators.matriz?.gee} />
        </div>

        <h2>Inadimplência</h2>
        {renderValues("inadimplencia", true, false)}
        <div className="pdf-chart-box">
          <Chart data={getChart("inadimplencia")} type="money" hideProjected={true} />
        </div>
      </section>

      <section className="pdf-section pdf-section--content">
        {renderPageDecoration()}
        <div className="pdf-header">
          <div className="pdf-divider">
            <h2>Indicadores de Endividamento</h2>
          </div>
        </div>

        <h2>Comprometimento Mensal (CML)</h2>
        {renderValues("cml", true, false)}
        <div className="pdf-chart-box">
          <Chart data={getChart("cml")} type="percent" />
        </div>

        <h2>Grau de Endividamento Econômico (GEE)</h2>
        {renderValues("gee", true, false)}
        <div className="pdf-chart-box">
          <Chart data={getChart("gee")} type="percent" />
        </div>
      </section>
    </div>
  );
};

export default PdfIndicators;
